import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandList,
} from "../../ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { cn } from "../../../lib/utils";
import { ChevronDown, Phone, Check } from "lucide-react";
import { getAllPhoneNumbers, getAccessiblePhoneNumbers } from "../../../apis/phoneInventory";
import Flag from "../../Flag";

function VirtualPhoneNumberSelect({
    selectedId = null,
    onChange,
    disabled = false,
    placeholder = "Select phone number",
    emptyMessage = "No phone numbers available",
    errorMessage = "Failed to load phone numbers",
    className,
    triggerClassName,
    workspaceId,
    preloadedNumbers = [],
    isAdmin = false,
}) {
    const [open, setOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
    const [numbers, setNumbers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [page, setPage] = useState(1);
    const [hasFetched, setHasFetched] = useState(false);
    const [selectedNumberData, setSelectedNumberData] = useState(null);

    const parentRef = useRef(null);
    const triggerRef = useRef(null);
    const abortControllerRef = useRef(null);
    const debounceTimerRef = useRef(null);

    // Debounce search query
    useEffect(() => {
        // Skip on initial mount - let the open handler trigger initial fetch
        if (!open) return;

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = setTimeout(() => {
            if (searchQuery !== debouncedSearchQuery) {
                setDebouncedSearchQuery(searchQuery);
                setPage(1);
                setNumbers([]);
                setHasMore(true);
                setHasFetched(false);
            }
        }, 300);

        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [searchQuery, open]);

    // Fetch phone numbers from API
    const fetchNumbers = useCallback(
        async (pageNum, search) => {
            if (!workspaceId) return;

            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            abortControllerRef.current = new AbortController();
            setLoading(true);
            setError(false);

            try {
                const queryParams = new URLSearchParams({
                    page: pageNum.toString(),
                    per_page: "20",
                });

                if (search) {
                    queryParams.append("filter[phone_number]", search);
                }

                const fetchFn = isAdmin ? getAllPhoneNumbers : getAccessiblePhoneNumbers;
                const response = await fetchFn({
                    query: queryParams.toString(),
                    options: {
                        headers: { "X-Workspace-Id": workspaceId },
                        signal: abortControllerRef.current.signal,
                    },
                });

                const newNumbers = (response?.data?.data || response?.data || []).map((number, index) => {
                    const location = [number.locality, number.region, number.country]
                        .filter(Boolean)
                        .join(", ") || "Unknown Location";

                    return {
                        id: number.id,
                        phone_number: number.phone_number,
                        label: number.label,
                        location: location,
                        type: number.type,
                        countryCode: number.country,
                        fullData: number,
                    };
                });

                const pagination = response?.data?.pagination || {};

                setNumbers((prev) => (pageNum === 1 ? newNumbers : [...prev, ...newNumbers]));
                setHasMore(pagination.current_page < pagination.total_pages || newNumbers.length === 20);
                setHasFetched(true);
                setLoading(false);
            } catch (err) {
                if (err.name !== "AbortError" && err.name !== "CanceledError") {
                    console.error("Error fetching phone numbers:", err);
                    setError(true);
                    setHasFetched(true);
                    setLoading(false);
                }
            }
        },
        [workspaceId, isAdmin]
    );

    useEffect(() => {
        if (open && !hasFetched && !loading) {
            fetchNumbers(1, debouncedSearchQuery);
        }
    }, [open, hasFetched, loading, fetchNumbers, debouncedSearchQuery]);

    const loadMore = useCallback(() => {
        if (!loading && hasMore) {
            const nextPage = page + 1;
            setPage(nextPage);
            fetchNumbers(nextPage, debouncedSearchQuery);
        }
    }, [loading, hasMore, page, fetchNumbers, debouncedSearchQuery]);

    const rowVirtualizer = useVirtualizer({
        count: numbers.length,
        getScrollElement: () => parentRef.current,
        estimateSize: () => 64,
        overscan: 5,
    });

    useEffect(() => {
        const [lastItem] = [...rowVirtualizer.getVirtualItems()].reverse();

        if (!lastItem) return;

        if (lastItem.index >= numbers.length - 1 && hasMore && !loading) {
            loadMore();
        }
    }, [rowVirtualizer.getVirtualItems(), numbers.length, hasMore, loading, loadMore]);

    useEffect(() => {
        if (disabled && open) {
            setOpen(false);
        }
    }, [disabled, open]);

    const numbersById = useMemo(() => {
        const map = new Map();

        // Normalize preloaded numbers to have consistent format
        preloadedNumbers.forEach((number) => {
            if (number && number.id) {
                map.set(number.id, {
                    ...number,
                    phone_number: number.phone_number || number.number,
                });
            }
        });

        numbers.forEach((number) => {
            if (number && number.id) {
                map.set(number.id, number);
            }
        });

        return map;
    }, [numbers, preloadedNumbers]);

    const selectedNumber = useMemo(() => {
        if (!selectedId) return null;
        if (selectedNumberData && selectedNumberData.id === selectedId) {
            return selectedNumberData;
        }
        if (numbersById.has(selectedId)) {
            return numbersById.get(selectedId);
        }
        return { id: selectedId, phone_number: selectedId, isPlaceholder: true };
    }, [selectedId, numbersById, selectedNumberData]);

    const handleSelect = (number) => {
        if (!onChange) return;
        setSelectedNumberData(number);
        onChange(number.id, number);
        setOpen(false);
    };

    const handleOpenChange = (nextOpen) => {
        if (disabled) return;
        setOpen(nextOpen);
        if (nextOpen) {
            // Always fetch fresh data when opening
            setPage(1);
            setHasMore(true);
            setHasFetched(false);
            setNumbers([]);
            setSearchQuery("");
            setDebouncedSearchQuery("");
        }
    };

    const handleSearchChange = (value) => {
        setSearchQuery(value);
    };

    return (
        <Popover open={open} onOpenChange={handleOpenChange}>
            <PopoverTrigger asChild>
                <button
                    ref={triggerRef}
                    type="button"
                    className={cn(
                        "flex w-full h-12 items-center justify-between rounded-lg border border-border bg-background px-3 py-2 text-left text-sm transition focus:outline-none",
                        disabled ? "cursor-not-allowed bg-gray-50 text-gray-400" : "hover:border-[#D0D5DD]",
                        triggerClassName,
                        className
                    )}
                    disabled={disabled}
                >
                    <div className="flex items-center space-x-2 flex-1 min-w-0">
                        {selectedNumber ? (
                            <div className="flex items-center space-x-2 min-w-0">
                                <span className="text-[24px] inline-flex items-center justify-center rounded-full overflow-hidden border border-border flex-shrink-0">
                                    <Flag code={selectedNumber.countryCode} squared />
                                </span>
                                <div className="min-w-0">
                                    <div className="font-medium text-gray-900 truncate">
                                        {selectedNumber.phone_number || selectedNumber.number || selectedNumber.id}
                                        {selectedNumber.label && (
                                            <span className="font-normal text-xs text-gray-500 ml-1">
                                                ({selectedNumber.label})
                                            </span>
                                        )}
                                    </div>
                                    {selectedNumber.location && (
                                        <div className="text-xs text-gray-500 truncate">
                                            {selectedNumber.location}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <>
                                <Phone className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                <span className="text-sm text-gray-500">
                                    {loading ? "Loading numbers..." : placeholder}
                                </span>
                            </>
                        )}
                    </div>

                    <div className="ml-2 flex items-center gap-2 text-gray-500 flex-shrink-0">
                        {loading && <div className="h-3 w-3 animate-spin rounded-full border-2 border-purple-200 border-t-purple-600" />}
                        <ChevronDown className="h-4 w-4" />
                    </div>
                </button>
            </PopoverTrigger>
            <PopoverContent
                className="p-0"
                align="start"
                style={{ width: triggerRef.current?.offsetWidth || 320 }}
            >
                <Command shouldFilter={false}>
                    <div className="p-2">
                        <div className="rounded-md border border-border">
                            <CommandInput
                                placeholder="Search phone numbers"
                                className="h-9"
                                value={searchQuery}
                                onValueChange={handleSearchChange}
                            />
                        </div>
                    </div>
                    <CommandList>
                        <div
                            ref={parentRef}
                            className="max-h-[300px] overflow-auto"
                        >
                            {error ? (
                                <div className="px-4 py-6 text-sm text-red-500">{errorMessage}</div>
                            ) : loading && numbers.length === 0 ? (
                                <div className="py-6 text-center text-sm text-gray-500">
                                    <div className="flex items-center justify-center gap-2">
                                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-purple-200 border-t-purple-600" />
                                        Loading phone numbers...
                                    </div>
                                </div>
                            ) : numbers.length === 0 && !loading ? (
                                <CommandEmpty className="py-6 text-center text-sm text-gray-500">
                                    {emptyMessage}
                                </CommandEmpty>
                            ) : (
                                <CommandGroup>
                                    <div
                                        style={{
                                            height: `${rowVirtualizer.getTotalSize()}px`,
                                            width: "100%",
                                            position: "relative",
                                        }}
                                    >
                                        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                                            const number = numbers[virtualRow.index];
                                            if (!number || !number.id) return null;

                                            const isSelected = selectedId === number.id;

                                            return (
                                                <div
                                                    key={number.id}
                                                    data-index={virtualRow.index}
                                                    ref={rowVirtualizer.measureElement}
                                                    style={{
                                                        position: "absolute",
                                                        top: 0,
                                                        left: 0,
                                                        width: "100%",
                                                        transform: `translateY(${virtualRow.start}px)`,
                                                    }}
                                                    className={cn(
                                                        "flex items-center justify-between gap-3 px-3 py-2 hover:bg-accent cursor-pointer",
                                                        isSelected && "bg-purple-50"
                                                    )}
                                                    onClick={() => handleSelect(number)}
                                                >
                                                    <div className="flex items-center space-x-2">
                                                        <span className="text-[24px] inline-flex items-center justify-center rounded-full overflow-hidden border border-border">
                                                            <Flag code={number.countryCode} squared />
                                                        </span>
                                                        <div>
                                                            <div className="font-semibold text-[#101828] dark:text-white text-sm">
                                                                {number.phone_number}
                                                                {number.label && (
                                                                    <span className="font-normal text-xs text-gray-500 ml-1">
                                                                        ({number.label})
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="text-xs text-[#535862] dark:text-gray-400 font-normal">
                                                                {number.location}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {isSelected && (
                                                        <Check className="h-4 w-4 text-purple-600 flex-shrink-0" />
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </CommandGroup>
                            )}
                            {loading && numbers.length > 0 && (
                                <div className="py-3 text-center text-sm text-gray-500">
                                    Loading more...
                                </div>
                            )}
                            {!hasMore && numbers.length > 0 && !loading && (
                                <div className="py-2 text-center text-xs text-gray-400">
                                    No more numbers
                                </div>
                            )}
                        </div>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}

export default VirtualPhoneNumberSelect;
